<template>
    <div class="animated fadeIn wilayah-page">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <div v-else>

            <!-- Header -->
            <div class="wilayah-header d-flex align-items-start justify-content-between mb-3">
                <div>
                    <h4 class="page-title mb-1">Master Wilayah</h4>
                    <p class="page-subtitle mb-0">Kelompokkan provinsi, kota, kecamatan, atau kelurahan menjadi satu wilayah kerja.</p>
                </div>
                <button type="button" class="btn btn-add-primary" name="tambahWilayahBtn" @click="openTambah()">
                    <i class="fa fa-plus mr-2"></i>Tambah Wilayah
                </button>
            </div>

            <!-- Tabel -->
            <div class="table-card">
                <div class="table-responsive-wrapper">
                    <b-table show-empty hover
                        :busy="isBusy"
                        :items="dataWilayah"
                        :fields="fields"
                        responsive class="custom-data-table mb-0">
                        <template v-slot:table-busy>
                            <div class="text-center text-muted my-4 py-3">
                                <b-spinner class="align-middle mr-2" variant="primary" small></b-spinner>
                                <span>Loading data...</span>
                            </div>
                        </template>
                        <template v-slot:empty>
                            <div class="text-center py-4 text-muted">
                                Belum ada wilayah terdaftar.
                            </div>
                        </template>

                        <template v-slot:cell(no)="row">
                            <span class="row-number">{{ padNumber(row.index + 1) }}</span>
                        </template>

                        <template v-slot:cell(nama)="row">
                            <span class="wilayah-name">{{ row.item.nama }}</span>
                        </template>

                        <template v-slot:cell(cakupan)="row">
                            <div class="chip-wrapper" v-if="row.item.cakupan && row.item.cakupan.length > 0">
                                <span class="area-chip" v-for="(area, idx) in row.item.cakupan" v-bind:key="idx">
                                    {{ area.level }} &ndash; {{ area.name }}
                                </span>
                            </div>
                            <span class="text-muted" v-else>-</span>
                        </template>

                        <template v-slot:cell(action)="row">
                            <div class="action-wrapper no-wrap">
                                <button type="button" class="btn btn-row-edit" name="ubahWilayahBtn" @click="openUbah(row.item, row.index)">Ubah</button>
                                <button type="button" class="btn btn-row-delete" name="hapusWilayahBtn" @click="confirmHapus(row.item)">Hapus</button>
                            </div>
                        </template>
                    </b-table>
                </div>

                <div class="table-total-footer d-flex align-items-center justify-content-between">
                    <span>Total <strong>{{ rows }}</strong> wilayah terdaftar.</span>
                    <b-pagination v-if="rows > perPage"
                        class="custom-pagination mb-0"
                        v-model="currentPage"
                        :total-rows="rows"
                        :per-page="perPage"
                        @input="onPageChange"
                        prev-text="&lsaquo;"
                        next-text="&rsaquo;"
                        hide-goto-end-buttons
                    ></b-pagination>
                </div>
            </div>
        </div>

        <!-- Modal tambah / ubah -->
        <b-modal id="modal-wilayah-form" content-class="pgi-modal wilayah-modal" size="lg"
            hide-header hide-footer no-close-on-backdrop @hidden="resetInput">

            <div class="wm-header">
                <div>
                    <h5 class="wm-title mb-1">{{ isEdit ? 'Ubah Wilayah' : 'Tambah Wilayah' }}</h5>
                    <p class="wm-subtitle mb-0">Pilih area dari hierarki Provinsi &rsaquo; Kota &rsaquo; Kecamatan &rsaquo; Kelurahan.</p>
                </div>
                <button type="button" class="wm-close" @click="$bvModal.hide('modal-wilayah-form')">
                    <i class="fa fa-times"></i>
                </button>
            </div>

            <div class="wm-body">
                <!-- Nama wilayah -->
                <div class="wm-field">
                    <label class="wm-label" for="namaWilayah">Wilayah <span class="wm-req">*</span></label>
                    <b-form-input
                        id="namaWilayah"
                        v-model="input.nama"
                        name="namaWilayah"
                        type="text"
                        placeholder="mis. JaBoDeTaBek"
                        class="wm-input"
                    ></b-form-input>
                </div>

                <!-- Cakupan area -->
                <div class="wm-field">
                    <div class="d-flex align-items-center justify-content-between">
                        <label class="wm-label mb-0">Cakupan Area <span class="wm-req">*</span></label>
                        <span class="wm-summary">
                            <strong>{{ selectedAreas.length }}</strong> area terpilih
                            <template v-if="kelurahanTermuat > 0"> &middot; mencakup <strong>{{ kelurahanTermuat }}</strong> kelurahan</template>
                        </span>
                    </div>

                    <!-- Chip terpilih -->
                    <div class="wm-chipbox" v-if="selectedAreas.length > 0">
                        <span class="wm-chip" v-for="(area, idx) in selectedAreas" v-bind:key="area.key">
                            <span class="wm-chip-text">
                                <span class="wm-chip-name">{{ area.name }}</span>
                                <span class="wm-chip-meta">{{ area.level }}<template v-if="area.level !== 'Kelurahan'"> &middot; seluruh {{ area.level.toLowerCase() }}</template></span>
                            </span>
                            <i class="fa fa-times" @click="hapusArea(idx)"></i>
                        </span>
                    </div>
                    <div class="wm-empty" v-else>Belum ada area dipilih.</div>

                    <!-- Pencarian -->
                    <div class="wm-search">
                        <i class="fa fa-search"></i>
                        <b-form-input
                            v-model="keyword"
                            name="cariArea"
                            type="search"
                            placeholder="Cari provinsi, kota, kecamatan, atau kelurahan..."
                            class="wm-search-input"
                        ></b-form-input>
                    </div>

                    <!-- Pohon area -->
                    <div class="wm-tree">
                        <div class="text-center py-4 text-muted" v-if="treeLoading">
                            <b-spinner class="align-middle mr-2" variant="primary" small></b-spinner>
                            <span>Memuat provinsi...</span>
                        </div>
                        <AreaTreeNode
                            v-else
                            v-for="node in treeRoot"
                            :key="node.key"
                            :node="node"
                            :selected="selectedAreas"
                            :taken="areaTerpakai"
                            :keyword="keyword"
                            :depth="0"
                            :on-toggle="toggleNode"
                            :on-expand="expandNode"
                        />
                    </div>

                    <p class="wm-hint mb-0">
                        Memilih provinsi atau kota otomatis mencakup seluruh kecamatan &amp; kelurahan di dalamnya &mdash;
                        yang tersimpan hanya area terluarnya. Tanda &ndash; berarti sebagian terpilih.
                        Daftar anak area dimuat saat node dibuka.
                    </p>
                </div>
            </div>

            <div class="wm-footer">
                <span class="wm-lock-reason" v-if="alasanTerkunci">
                    <i class="fa fa-info-circle mr-1"></i>{{ alasanTerkunci }}
                </span>
                <button type="button" class="btn wm-btn-cancel" name="batalWilayahBtn" @click="$bvModal.hide('modal-wilayah-form')">Batal</button>
                <button type="button" class="btn wm-btn-submit" name="simpanWilayahBtn" :disabled="!bolehSimpan" @click="onSubmit()">
                    {{ isEdit ? 'Simpan Perubahan' : 'Tambah Wilayah' }}
                </button>
            </div>
        </b-modal>

        <ModalConfirm id="modal-confirm-hapus-wilayah" title="Hapus Wilayah" content-class="pgi-modal" :isSubmitting="isSubmitting"
            desc="Apakah anda yakin untuk menghapus wilayah ini?" @submit="onHapus" />
    </div>
</template>

<script>
import AreaTreeNode from './AreaTreeNode'

// Endpoint anak per tingkat. Anak dimuat hanya saat node dibuka (lazy load),
// supaya modal tidak perlu menarik seluruh hierarki di awal.
const CHILD_URL = {
    Provinsi: id => `area/kota/${id}`,
    Kota: id => `area/kecamatan/${id}`,
    Kecamatan: id => `area/kelurahan/${id}`,
}
const CHILD_LEVEL = { Provinsi: 'Kota', Kota: 'Kecamatan', Kecamatan: 'Kelurahan' }

export default {
    name: 'master-wilayah',
    components: { AreaTreeNode },
    data() {
        return {
            initStatus: 0,
            isBusy: false,
            isSubmitting: false,
            isEdit: false,
            editIndex: null,
            editId: null,
            deleteIndex: null,

            fields: [
                { key: "no", label: "No", class: "col-no" },
                { key: "nama", label: "Wilayah" },
                { key: "cakupan", label: "Cakupan Area" },
                { key: "action", label: "Aksi", class: "text-right" },
            ],

            // Pohon area
            treeRoot: [],
            treeLoading: false,
            keyword: "",

            // Area terluar yang dipilih. Tiap item: { key, id, name, level, path }
            selectedAreas: [],

            // Area milik wilayah LAIN: { key, path, wilayahNama }.
            // Dipakai mengunci pilihan supaya satu area tidak masuk dua wilayah.
            areaTerpakai: [],

            input: { nama: "" },

            dataWilayah: [],

            // Paginasi daftar wilayah
            currentPage: 1,
            perPage: 10,
            rows: 0,
            page: 0,
            size: 10,

            deleteId: null
        }
    },
    computed: {

        bolehSimpan() {
            return this.alasanTerkunci === null
        },

        // Ditampilkan di footer supaya jelas kenapa tombol simpan belum aktif.
        alasanTerkunci() {
            if (!this.$helper.isNotNullAndNotEmpty(this.input.nama)) return "Isi nama wilayah dulu"
            if (this.selectedAreas.length === 0) return "Pilih minimal satu area"
            return null
        },

        // Hanya menghitung kelurahan dari cabang yang sudah dimuat.
        // Jumlah sebenarnya bisa lebih besar bila node belum pernah dibuka.
        kelurahanTermuat() {
            let total = 0
            const walk = (nodes) => {
                for (let n of nodes) {
                    let tercakup = this.selectedAreas.some(sel => sel.key === n.key || n.path.indexOf(sel.key) !== -1)
                    if (n.level === 'Kelurahan' && tercakup) total++
                    if (n.children.length > 0) walk(n.children)
                }
            }
            walk(this.treeRoot)
            return total
        }

    },
    methods: {

        //----------------- Fungsi Inisialisasi -----------------

        init() {
            this.initStatus = 0
            this.getDataWilayah().then(() => {
                this.initStatus = 1
            })
            .catch(error => {
                console.log(error)
                this.initStatus = -1
            })
        },

        getDataWilayah() {
            this.isBusy = true
            return this.$axios.get(`wilayah/?page=${this.page}&size=${this.size}`).then(response => {
                this.dataWilayah = response.data.data
                this.updateTotalItem(response.data.total)
            })
            .finally(() => {
                this.isBusy = false
            })
        },

        onPageChange() {
            this.page = this.currentPage - 1
            this.getDataWilayah().catch(error => this.$helper.parseError(this, error))
        },

        updateTotalItem(total) {
            this.rows = total
        },

        //----------------- Pohon Area (lazy load) -----------------

        buatNode(item, level, path) {
            return {
                key: `${level}:${item.id}`,
                id: item.id,
                name: item.name,
                level: level,
                path: path,
                children: [],
                loaded: false,
                loading: false,
                expanded: false,
            }
        },

        loadAreaTerpakai(excludeId) {
            let url = excludeId != null ? `wilayah/area-terpakai?excludeWilayahId=${excludeId}` : "wilayah/area-terpakai"
            return this.$axios.get(url).then(response => {
                this.areaTerpakai = response.data.map(a => ({
                    key: `${this.levelTitle(a.level)}:${a.areaId}`,
                    path: (a.path || []).map(k => {
                        let [lvl, id] = k.split(":")
                        return `${this.levelTitle(lvl)}:${id}`
                    }),
                    wilayahNama: a.wilayahNama
                }))
            })
            .catch(error => {
                console.log(error)
                this.$helper.parseError(this, error)
            })
        },

        loadProvinsi() {
            if (this.treeRoot.length > 0) return
            this.treeLoading = true
            this.$axios.get("area/provinsi/").then(response => {
                this.treeRoot = response.data.map(it => this.buatNode(it, 'Provinsi', []))
            })
            .catch(error => {
                console.log(error)
                this.$helper.parseError(this, error)
            })
            .finally(() => {
                this.treeLoading = false
            })
        },

        expandNode(node) {
            if (node.level === 'Kelurahan') return

            // Tutup bila sedang terbuka
            if (node.expanded) {
                node.expanded = false
                return
            }

            // Sudah pernah dimuat -> cukup buka
            if (node.loaded) {
                node.expanded = true
                return
            }

            node.loading = true
            this.$axios.get(CHILD_URL[node.level](node.id)).then(response => {
                let levelAnak = CHILD_LEVEL[node.level]
                let path = node.path.concat([node.key])
                node.children = response.data.map(it => this.buatNode(it, levelAnak, path))
                node.loaded = true
                node.expanded = true
            })
            .catch(error => {
                console.log(error)
                this.$helper.parseError(this, error)
            })
            .finally(() => {
                node.loading = false
            })
        },

        toggleNode(node) {
            // Sudah tercakup leluhur -> harus dilepas di level leluhurnya
            let leluhurTerpilih = this.selectedAreas.some(s => node.path.indexOf(s.key) !== -1)
            if (leluhurTerpilih) {
                this.$helper.toastErr(this, "Area ini sudah tercakup oleh pilihan di atasnya. Hapus centang di level tersebut.")
                return
            }

            // Area milik wilayah lain tidak boleh dipilih
            let pemilik = this.areaTerpakai.find(t =>
                t.key === node.key ||
                node.path.indexOf(t.key) !== -1 ||
                (t.path || []).indexOf(node.key) !== -1
            )
            if (pemilik) {
                this.$helper.toastErr(this, `Area ini sudah dipakai wilayah "${pemilik.wilayahNama}". Satu area hanya boleh masuk satu wilayah.`)
                return
            }

            let idx = this.selectedAreas.findIndex(s => s.key === node.key)
            if (idx !== -1) {
                this.selectedAreas.splice(idx, 1)
                return
            }

            // Pilih node ini, buang keturunannya yang sudah terpilih
            // karena kini sudah tercakup olehnya.
            this.selectedAreas = this.selectedAreas.filter(s => s.path.indexOf(node.key) === -1)
            this.selectedAreas.push({
                key: node.key,
                id: node.id,
                name: node.name,
                level: node.level,
                path: node.path.slice()
            })
        },

        hapusArea(idx) {
            this.selectedAreas.splice(idx, 1)
        },

        //----------------- Tambah / Ubah / Hapus -----------------

        resetInput() {
            this.input.nama = ""
            this.selectedAreas = []
            this.areaTerpakai = []
            this.keyword = ""
        },

        openTambah() {
            this.isEdit = false
            this.editIndex = null
            this.editId = null
            this.resetInput()
            this.loadProvinsi()
            this.loadAreaTerpakai(null)
            this.$bvModal.show("modal-wilayah-form")
        },

        openUbah(item, index) {
            this.isEdit = true
            this.editIndex = index
            this.editId = item.id
            this.resetInput()
            this.input.nama = item.nama
            // Bentuk dari API: { level, areaId, name }. Diubah ke bentuk pohon
            // { key, id, name, level, path } supaya centangnya cocok.
            this.selectedAreas = (item.cakupan || []).map(a => ({
                key: `${this.levelTitle(a.level)}:${a.areaId}`,
                id: a.areaId,
                name: a.name,
                level: this.levelTitle(a.level),
                path: []
            }))
            this.loadProvinsi()
            this.loadAreaTerpakai(this.editId)
            this.$bvModal.show("modal-wilayah-form")
        },

        onSubmit() {
            if (!this.$helper.isNotNullAndNotEmpty(this.input.nama)) {
                this.$helper.toastErr(this, "Mohon isi nama wilayah!")
                return
            }
            if (this.selectedAreas.length === 0) {
                this.$helper.toastErr(this, "Mohon pilih minimal satu area!")
                return
            }

            this.isSubmitting = true

            let payload = new FormData()
            if (this.isEdit && this.editId != null) payload.append("id", this.editId)
            payload.append("name", this.input.nama)
            // areaLevel & areaId dikirim sebagai dua list sejajar
            this.selectedAreas.forEach(a => {
                payload.append("areaLevel", a.level.toUpperCase())
                payload.append("areaId", a.id)
            })

            this.$axios.post("wilayah/", payload).then(() => {
                this.$helper.toastSucc(this, this.isEdit ? "Wilayah berhasil diubah!" : "Wilayah berhasil ditambahkan!")
                this.$bvModal.hide("modal-wilayah-form")
                return this.getDataWilayah()
            })
            .catch(error => {
                console.log(error)
                this.$helper.parseError(this, error)
            })
            .finally(() => {
                this.isSubmitting = false
            })
        },

        confirmHapus(item) {
            this.deleteId = item.id
            this.$bvModal.show("modal-confirm-hapus-wilayah")
        },

        onHapus() {
            this.isSubmitting = true

            let payload = new FormData()
            payload.append("id", this.deleteId)

            this.$axios.post("wilayah/delete", payload).then(() => {
                this.$helper.toastSucc(this, "Wilayah berhasil dihapus!")
                this.$bvModal.hide("modal-confirm-hapus-wilayah")
                this.deleteId = null
                return this.getDataWilayah()
            })
            .catch(error => {
                console.log(error)
                this.$helper.parseError(this, error)
            })
            .finally(() => {
                this.isSubmitting = false
            })
        },

        //----------------- Fungsi Lain -----------------

        padNumber(n) {
            return n < 10 ? `0${n}` : `${n}`
        },

        // Backend menyimpan level dalam huruf besar (PROVINSI),
        // pohon area memakai bentuk berkapital (Provinsi).
        levelTitle(level) {
            if (!level) return level
            return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase()
        }

    },
    created() {
        this.init()
    }
}
</script>

<style lang="scss" scoped>
// Design System Tokens (Deep Pine Palette)
$deep-pine: #023831;
$deep-pine-hover: #0A5247;
$ink-body: #16302B;
$ink-heading: #072D28;
$ink-muted: #7E918B;
$line-border: #E2E7E3;
$input-border: #D8E2DC;
$danger-ink: #9B4A3C;

.wilayah-page { padding-bottom: 30px; }

.wilayah-header {
    .page-title {
        font-size: 22px;
        font-weight: 700;
        color: $ink-heading;
        letter-spacing: -0.01em;
    }

    .page-subtitle {
        font-size: 13px;
        color: $ink-muted;
        line-height: 1.45;
    }
}

.btn-add-primary {
    background-color: $deep-pine;
    color: #FFFFFF;
    font-size: 13px;
    font-weight: 600;
    padding: 10px 20px;
    border-radius: 8px;
    border: none;
    white-space: nowrap;
    transition: all 0.2s ease;

    &:hover {
        background-color: $deep-pine-hover;
        color: #FFFFFF;
        transform: translateY(-1px);
        box-shadow: 0 4px 10px rgba(2, 56, 49, 0.15);
    }
}

.table-card {
    background-color: #FFFFFF;
    border: 1px solid $line-border;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    padding: 0;
    overflow: hidden;
}

.table-responsive-wrapper { overflow-x: auto; }

.custom-data-table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;

    ::v-deep thead th {
        background-color: #F6F7F6;
        border-top: none;
        border-bottom: 1px solid #ECEFEC;
        font-size: 11px;
        font-weight: 600;
        color: $ink-muted;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        padding: 14px 20px;
        vertical-align: middle;
        white-space: nowrap;
    }

    ::v-deep tbody tr {
        transition: background-color 0.15s ease;

        &:hover { background-color: #F8FAF9; }

        td {
            padding: 18px 20px;
            border-top: 1px solid #EDF2EF;
            border-bottom: none;
            vertical-align: middle;
            font-size: 13px;
            color: $ink-body;
        }

        &:first-child td { border-top: none; }
    }

    ::v-deep .col-no { width: 70px; }
}

.row-number {
    font-size: 12.5px;
    color: #A9B7B1;
    font-weight: 500;
    letter-spacing: 0.04em;
}

.wilayah-name {
    font-size: 14px;
    font-weight: 700;
    color: $ink-heading;
}

// Chip cakupan area
.chip-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.area-chip {
    display: inline-flex;
    align-items: center;
    background-color: #EDF3EF;
    color: #2A4B42;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;

    &.removable i {
        cursor: pointer;
        color: $ink-muted;
        font-size: 11px;

        &:hover { color: $danger-ink; }
    }
}

// Tombol aksi baris
.action-wrapper {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.btn-row-edit,
.btn-row-delete {
    background-color: #FFFFFF;
    border: 1px solid $input-border;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 500;
    padding: 7px 18px;
    transition: all 0.2s ease;
}

.btn-row-edit {
    color: $ink-body;

    &:hover { background-color: #F4F7F5; border-color: #BAC9C2; color: $ink-heading; }
}

.btn-row-delete {
    color: $danger-ink;

    &:hover { background-color: #F9EDEB; border-color: #E4C4BD; color: $danger-ink; }
}

.table-total-footer {
    border-top: 1px solid #EDF2EF;
    padding: 16px 20px;
    font-size: 12.5px;
    color: $ink-muted;

    strong { color: $ink-heading; font-weight: 700; }
}

.custom-pagination {
    ::v-deep .page-item {
        margin: 0 2px;

        .page-link {
            border-radius: 6px !important;
            border: 1px solid $input-border;
            color: $ink-body;
            font-size: 12px;
            font-weight: 500;
            min-width: 30px;
            height: 30px;
            padding: 0 8px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background-color: #FFFFFF;
            box-shadow: none !important;

            &:hover { background-color: #F4F7F5; border-color: #BAC9C2; }
        }

        &.active .page-link {
            background-color: $deep-pine !important;
            border-color: $deep-pine !important;
            color: #FFFFFF !important;
            font-weight: 700;
        }

        &.disabled .page-link { opacity: 0.45; cursor: not-allowed; }
    }
}

// ============================================================
// Modal Tambah / Ubah Wilayah
// ============================================================
::v-deep .wilayah-modal { border-radius: 14px; }

.wm-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 24px 26px 18px;

    .wm-title {
        font-size: 18px;
        font-weight: 700;
        color: $ink-heading;
        letter-spacing: -0.01em;
    }

    .wm-subtitle {
        font-size: 12.5px;
        color: $ink-muted;
    }

    .wm-close {
        background: transparent;
        border: none;
        color: $ink-muted;
        font-size: 16px;
        line-height: 1;
        padding: 4px 6px;
        cursor: pointer;

        &:hover { color: $ink-heading; }
    }
}

.wm-body { padding: 0 26px 4px; }

.wm-field { margin-bottom: 22px; }

.wm-label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: $ink-muted;
    margin-bottom: 8px;
}

.wm-req { color: #C0803A; }

.wm-summary {
    font-size: 11.5px;
    color: $ink-muted;

    strong { color: $ink-heading; font-weight: 700; }
}

::v-deep .wm-input {
    height: 46px;
    border-radius: 10px;
    border: 1px solid $input-border;
    font-size: 14px;
    color: $ink-body;
    padding: 10px 16px;

    &::placeholder { color: #A9B7B1; }

    &:focus {
        border-color: $deep-pine;
        box-shadow: 0 0 0 2px rgba(2, 56, 49, 0.1);
        outline: none;
    }
}

// Chip area terpilih
.wm-chipbox {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    background-color: #F7FAF8;
    border: 1px solid #EAF0EC;
    border-radius: 10px;
    padding: 12px;
    margin-top: 8px;
}

.wm-chip {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background-color: #FFFFFF;
    border: 1px solid #E2E7E3;
    border-radius: 8px;
    padding: 7px 12px;

    .wm-chip-text { display: flex; flex-direction: column; line-height: 1.25; }
    .wm-chip-name { font-size: 13px; font-weight: 700; color: $ink-heading; }
    .wm-chip-meta { font-size: 10.5px; color: $ink-muted; }

    i {
        cursor: pointer;
        color: $ink-muted;
        font-size: 11px;

        &:hover { color: $danger-ink; }
    }
}

.wm-empty {
    border: 1px dashed $input-border;
    border-radius: 10px;
    padding: 14px 16px;
    background-color: #FAFCFA;
    color: #8C9F98;
    font-size: 12.5px;
    margin-top: 8px;
}

// Pencarian
.wm-search {
    position: relative;
    margin-top: 12px;

    i {
        position: absolute;
        left: 14px;
        top: 50%;
        transform: translateY(-50%);
        color: #A9B7B1;
        font-size: 12px;
        z-index: 2;
    }

    ::v-deep .wm-search-input {
        height: 42px;
        border-radius: 10px;
        border: 1px solid $input-border;
        font-size: 13px;
        color: $ink-body;
        padding: 8px 14px 8px 36px;

        &::placeholder { color: #A9B7B1; }

        &:focus {
            border-color: $deep-pine;
            box-shadow: 0 0 0 2px rgba(2, 56, 49, 0.1);
            outline: none;
        }
    }
}

// Pohon area
.wm-tree {
    margin-top: 12px;
    border: 1px solid $input-border;
    border-radius: 10px;
    max-height: 260px;
    overflow-y: auto;
    background-color: #FFFFFF;
}

.wm-hint {
    margin-top: 10px;
    font-size: 11.5px;
    color: $ink-muted;
    line-height: 1.5;
}

// Footer
.wm-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;

    .wm-lock-reason {
        margin-right: auto;
        font-size: 12px;
        color: #9A6B2F;
    }

    padding: 18px 26px 22px;
    border-top: 1px solid #EDF2EF;
    margin-top: 12px;
}

.wm-btn-cancel {
    background-color: #FFFFFF;
    border: 1px solid $input-border;
    color: $ink-body;
    border-radius: 8px;
    padding: 10px 24px;
    font-size: 13px;
    font-weight: 500;

    &:hover { background-color: #F4F7F5; border-color: #BAC9C2; color: $ink-heading; }
}

.wm-btn-submit {
    background-color: $deep-pine;
    border: 1px solid $deep-pine;
    color: #FFFFFF;
    border-radius: 8px;
    padding: 10px 24px;
    font-size: 13px;
    font-weight: 700;

    &:hover:not(:disabled) { background-color: $deep-pine-hover; color: #FFFFFF; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
}

</style>
